import { createPersistedStore } from 'features/load-hub/hooks/createPersistedStore'

const STORAGE_KEY = 'box_truck_dat_enabled'

/** How long an operator's "DAT on" choice survives before the board falls back to its OFF default. */
export const DAT_CHOICE_TTL_MS = 24 * 60 * 60 * 1000

/** What gets stored: WHEN the operator switched DAT on. A date rather than a flag is what makes the choice
 *  expirable — a bare `true` can only be kept forever or thrown away on sight. */
interface StoredDatChoice {
    at?: unknown
}

/**
 * Whether a stored choice still counts as "DAT on".
 *
 * Anything undatable reads as OFF: no entry, a value that is not JSON, an older boolean format, an object
 * with no `at`, or an `at` that is not a number. Falling back to the default is the safe direction — the
 * board simply stops asking for a paid feed, which the operator can undo in one click.
 */
export const datChoiceIsLive = (raw: string | null, now: number = Date.now()): boolean => {
    if (!raw) return false
    try {
        const stored = JSON.parse(raw) as StoredDatChoice | null
        if (typeof stored?.at !== 'number' || !Number.isFinite(stored.at)) return false
        // A future timestamp means a clock that moved backwards, not a choice made later. Treat the elapsed
        // time as zero rather than letting it read as expired.
        return now - stored.at < DAT_CHOICE_TTL_MS
    } catch {
        return false
    }
}

/** Stamps the moment DAT was switched ON, so the day starts again each time. Off stores nothing to date. */
export const stampDatChoice = (enabled: boolean, now: number = Date.now()): string => (enabled ? JSON.stringify({ at: now }) : '')

/**
 * "DAT loads" — the board's opt-in live supplement, OFF by default and switched from Advanced Filters.
 *
 * DAT is a LIVE one.dat.com search on the shared token pool, so an operator who leaves it on costs a real
 * external call on every search and auto-merge, whether or not they were reading the rows. Sylectus is our
 * own Postgres and stays free. Defaulting to off means the expensive half is only paid for when asked for.
 *
 * The choice is remembered so enabling it is not a per-search chore, but it EXPIRES after a day: an operator
 * who turned DAT on for one afternoon's lane should not still be paying for it a week later having forgotten
 * it was ever on.
 *
 * The expiry is evaluated when this module first loads — i.e. per page load — not on a timer. A board left
 * open past the deadline keeps the feed it already has; the point is to reset a forgotten choice, not to pull
 * a live feed out from under someone mid-session.
 */
export const useBoxTruckDatEnabled = createPersistedStore<boolean>(STORAGE_KEY, datChoiceIsLive, stampDatChoice)
